import React, { useState } from 'react';
import { grcService } from '../../services/grcService';
import { AIApplicabilityResult } from '../../types/grc';
import { 
  Brain, Sparkles, Building2, Users, Globe, Shield, 
  Database, ArrowRight, CheckCircle, XCircle, Loader2,
  AlertCircle, ChevronRight, ChevronDown
} from 'lucide-react';

interface AIApplicabilityWizardProps {
  frameworkId: number;
  frameworkName: string;
  onComplete: (results: AIApplicabilityResult[]) => void;
  onClose: () => void;
}

interface WizardQuestion {
  id: string;
  question: string;
  type: 'text' | 'select' | 'multiselect';
  options?: { label: string; value: string }[];
  icon: React.ElementType;
  placeholder?: string;
  helpText?: string;
}

const WIZARD_QUESTIONS: WizardQuestion[] = [
  {
    id: 'organization_name',
    question: 'What is your organization name?',
    type: 'text',
    icon: Building2,
    placeholder: 'e.g., Acme Corporation'
  },
  {
    id: 'industry',
    question: 'What industry does your organization operate in?',
    type: 'select',
    icon: Globe,
    options: [
      { label: 'Financial Services / Banking', value: 'financial' },
      { label: 'Healthcare', value: 'healthcare' },
      { label: 'Technology / IT Services', value: 'technology' },
      { label: 'Manufacturing', value: 'manufacturing' },
      { label: 'Retail / E-commerce', value: 'retail' },
      { label: 'Government / Public Sector', value: 'government' },
      { label: 'Education', value: 'education' },
      { label: 'Telecommunications', value: 'telecom' },
      { label: 'Energy / Utilities', value: 'energy' },
      { label: 'Gaming / Betting', value: 'gaming' },
      { label: 'Other', value: 'other' }
    ]
  },
  {
    id: 'size',
    question: 'What is the size of your organization?',
    type: 'select',
    icon: Users,
    options: [
      { label: 'Micro (1-10 employees)', value: 'micro' },
      { label: 'Small (11-50 employees)', value: 'small' },
      { label: 'Medium (51-250 employees)', value: 'medium' },
      { label: 'Large (251-1000 employees)', value: 'large' },
      { label: 'Enterprise (1000+ employees)', value: 'enterprise' }
    ]
  },
  {
    id: 'services',
    question: 'What services or products does your organization provide?',
    type: 'text',
    icon: Shield,
    placeholder: 'e.g., Online banking, payment processing, customer data management...',
    helpText: 'Describe your main business activities and services'
  },
  {
    id: 'data_types',
    question: 'What types of data does your organization process?',
    type: 'multiselect',
    icon: Database,
    options: [
      { label: 'Personal Identifiable Information (PII)', value: 'pii' },
      { label: 'Financial / Payment Data', value: 'financial' },
      { label: 'Health Records (PHI)', value: 'health' },
      { label: 'Intellectual Property', value: 'ip' },
      { label: 'Customer Business Data', value: 'customer_business' },
      { label: 'Employee Data', value: 'employee' },
      { label: 'Public Data Only', value: 'public' }
    ]
  },
  {
    id: 'regulatory_requirements',
    question: 'What regulatory requirements apply to your organization?',
    type: 'multiselect',
    icon: Shield,
    options: [
      { label: 'GDPR (EU Data Protection)', value: 'gdpr' },
      { label: 'PCI DSS (Payment Card)', value: 'pci' },
      { label: 'HIPAA (Health Data)', value: 'hipaa' },
      { label: 'SOX (Financial Reporting)', value: 'sox' },
      { label: 'Local Data Protection Laws', value: 'local_dp' },
      { label: 'Industry-specific regulations', value: 'industry' },
      { label: 'None / Unsure', value: 'none' }
    ]
  }
];

export const AIApplicabilityWizard: React.FC<AIApplicabilityWizardProps> = ({
  frameworkId,
  frameworkName,
  onComplete,
  onClose
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<AIApplicabilityResult[] | null>(null);
  const [summary, setSummary] = useState<string>('');
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

  const currentQuestion = WIZARD_QUESTIONS[currentStep];
  const totalSteps = WIZARD_QUESTIONS.length;
  const isLastQuestion = currentStep === totalSteps - 1;

  const handleAnswer = (value: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const handleMultiselectToggle = (value: string) => {
    const current = (answers[currentQuestion.id] as string[]) || [];
    if (current.includes(value)) {
      handleAnswer(current.filter(v => v !== value));
    } else {
      handleAnswer([...current, value]);
    }
  };

  const handleNext = async () => {
    if (isLastQuestion) {
      await runAIAssessment();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const runAIAssessment = async () => {
    setProcessing(true);
    try {
      const result = await grcService.runAIApplicabilityAssessment(frameworkId, {
        organization_name: answers.organization_name as string || 'Unknown',
        industry: answers.industry as string || 'other',
        size: answers.size as string || 'medium',
        services: answers.services as string || '',
        data_types: Array.isArray(answers.data_types) ? answers.data_types.join(', ') : '',
        regulatory_requirements: Array.isArray(answers.regulatory_requirements) ? answers.regulatory_requirements.join(', ') : ''
      });

      if (result.success) {
        setResults(result.results);
        setSummary(result.summary);
      } else {
        alert('AI assessment failed: ' + result.summary);
      }
    } catch (error) {
      alert('Error running AI assessment');
      console.error(error);
    }
    setProcessing(false);
  };

  const handleApplyResults = () => {
    if (results) {
      onComplete(results);
    }
  };

  const toggleResultExpand = (controlId: number) => {
    const newExpanded = new Set(expandedResults);
    if (newExpanded.has(controlId)) {
      newExpanded.delete(controlId);
    } else {
      newExpanded.add(controlId);
    }
    setExpandedResults(newExpanded);
  };

  const canProceed = () => {
    const answer = answers[currentQuestion?.id];
    if (!answer) return false;
    if (Array.isArray(answer)) return answer.length > 0;
    return answer.trim().length > 0;
  };

  // Results view
  if (results) {
    const applicable = results.filter(r => r.applicable);
    const notApplicable = results.filter(r => !r.applicable);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Brain className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">AI Applicability Assessment Complete</h2>
                <p className="text-purple-100 text-sm">{frameworkName}</p>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-800">
            <p className="text-sm text-purple-800 dark:text-purple-300">{summary}</p>
            <div className="flex gap-6 mt-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">{applicable.length} Applicable</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium">{notApplicable.length} Not Applicable</span>
              </div>
            </div>
          </div>

          {/* Results List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {results.map((result) => (
              <div
                key={result.control_id}
                className={`rounded-lg border transition-colors ${
                  result.applicable
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                }`}
              >
                <button
                  onClick={() => toggleResultExpand(result.control_id)}
                  className="w-full p-3 flex items-center gap-3 text-left"
                >
                  {result.applicable ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        Control #{result.control_id}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        result.confidence >= 80 ? 'bg-green-100 text-green-800' :
                        result.confidence >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {result.confidence}% confidence
                      </span>
                    </div>
                  </div>
                  {expandedResults.has(result.control_id) ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {expandedResults.has(result.control_id) && (
                  <div className="px-3 pb-3 pl-11">
                    <p className="text-sm text-gray-600 dark:text-gray-300">{result.reason}</p>
                    {result.suggested_owner && (
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="font-medium">Suggested Owner:</span> {result.suggested_owner}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyResults}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Apply to Controls
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Processing view
  if (processing) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            AI is Analyzing Applicability
          </h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Based on your organization profile, AI is determining which controls are applicable to your context...
          </p>
          <div className="mt-6 flex justify-center gap-1">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Wizard questions view
  const Icon = currentQuestion?.icon || Building2;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold">AI Applicability Wizard</h2>
              <p className="text-purple-100 text-sm">{frameworkName}</p>
            </div>
          </div>
          
          {/* Progress */}
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1 rounded ${
                  i <= currentStep ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-purple-200 mt-2">
            Step {currentStep + 1} of {totalSteps}
          </p>
        </div>

        {/* Question Content */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentQuestion?.question}
              </h3>
              {currentQuestion?.helpText && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {currentQuestion.helpText}
                </p>
              )}
            </div>
          </div>

          {/* Input based on type */}
          {currentQuestion?.type === 'text' && (
            <input
              type="text"
              value={(answers[currentQuestion.id] as string) || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder={currentQuestion.placeholder}
              className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-xl text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          )}

          {currentQuestion?.type === 'select' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {currentQuestion.options?.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(opt.value)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    answers[currentQuestion.id] === opt.value
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'border-gray-200 dark:border-gray-600 hover:border-purple-300 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {currentQuestion?.type === 'multiselect' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {currentQuestion.options?.map(opt => {
                const selected = ((answers[currentQuestion.id] as string[]) || []).includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleMultiselectToggle(opt.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      selected
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'border-gray-200 dark:border-gray-600 hover:border-purple-300 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                    }`}>
                      {selected && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button
            onClick={currentStep === 0 ? onClose : handleBack}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </button>
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLastQuestion ? (
              <>
                <Brain className="w-4 h-4" />
                Run AI Assessment
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
